import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import OutgoingMailIcon from "@mui/icons-material/ForwardToInbox";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useLoaderData, useRevalidator, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import MuiDatePicker from "../components/MuiDatePicker";
import { Result } from "../../types/Result";
import { confirmUnconfirmed, defaultSince, queueRecoveryMail } from "./api";
import { BulkConfirmResult, RecoveryMailResult, UnconfirmedUser, UnconfirmedUsersSummary } from "./type";

const columns: GridColDef<UnconfirmedUser>[] = [
  { field: "email", headerName: "E-posta", flex: 1, minWidth: 220 },
  { field: "fullName", headerName: "Ad Soyad", flex: 1, minWidth: 160 },
  {
    field: "creationTime",
    headerName: "Kayıt Tarihi",
    type: "dateTime",
    width: 170,
    valueGetter: (v) => (v ? new Date(v) : null),
  },
  { field: "recoveryMailQueued", headerName: "Kurtarma maili kuyrukta", type: "boolean", width: 170 },
  { field: "recoveryMailSent", headerName: "Kurtarma maili gönderildi", type: "boolean", width: 180 },
];

const errorsOf = (e: any): string =>
  (e?.response?.data?.errors as string[] | undefined)?.join(" | ") || e?.message || "Bir hata oluştu";

function SummaryCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <Card variant="outlined" sx={{ flex: 1 }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function UnconfirmedUsersPanel() {
  const data = useLoaderData() as Result<UnconfirmedUsersSummary>;
  const revalidator = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();
  const summary = data.data;

  const since: Dayjs = useMemo(() => {
    const raw = searchParams.get("since");
    return raw ? dayjs(raw) : defaultSince();
  }, [searchParams]);

  const [selection, setSelection] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });

  const users = useMemo(() => summary?.users ?? [], [summary]);
  const selectedIds: string[] = useMemo(() => {
    if (selection.type === "include") return Array.from(selection.ids).map(String);
    return users.filter((u) => !selection.ids.has(u.userId)).map((u) => u.userId);
  }, [selection, users]);

  // --- Kurtarma maili
  const [mailOpen, setMailOpen] = useState(false);
  const [maxCount, setMaxCount] = useState(500);
  const [perHour, setPerHour] = useState(300);
  const [includeNotified, setIncludeNotified] = useState(false);
  const [mailPreview, setMailPreview] = useState<RecoveryMailResult | null>(null);
  const [busy, setBusy] = useState(false);

  const runRecoveryMail = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const res = await queueRecoveryMail({
        since: since.toISOString(),
        maxCount,
        perHour,
        includeAlreadyNotified: includeNotified,
        dryRun,
      });
      if (!res.data.isSuccess || !res.data.data) {
        toast.error(res.data.errors?.[0] || "İşlem başarısız");
        return;
      }
      if (dryRun) {
        setMailPreview(res.data.data);
      } else {
        const r = res.data.data;
        toast.success(
          `${r.queued} kullanıcı için kurtarma maili kuyruğa alındı. Son gönderim: ${
            r.lastSendAt ? new Date(r.lastSendAt).toLocaleString("tr-TR") : "-"
          }`
        );
        setMailOpen(false);
        setMailPreview(null);
        revalidator.revalidate();
      }
    } catch (e) {
      toast.error(errorsOf(e));
    } finally {
      setBusy(false);
    }
  };

  // --- Toplu onay
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState<BulkConfirmResult | null>(null);
  const confirmScopeSelected = selectedIds.length > 0;

  const runConfirm = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const res = await confirmUnconfirmed(
        confirmScopeSelected ? { userIds: selectedIds, dryRun } : { since: since.toISOString(), dryRun }
      );
      if (!res.data.isSuccess || !res.data.data) {
        toast.error(res.data.errors?.[0] || "İşlem başarısız");
        return;
      }
      if (dryRun) {
        setConfirmPreview(res.data.data);
      } else {
        toast.success(`${res.data.data.confirmed} kullanıcının e-postası onaylandı`);
        setConfirmOpen(false);
        setConfirmPreview(null);
        setSelection({ type: "include", ids: new Set() });
        revalidator.revalidate();
      }
    } catch (e) {
      toast.error(errorsOf(e));
    } finally {
      setBusy(false);
    }
  };

  if (!summary) {
    return <Alert severity="error">{data.errors?.[0] || "Liste yüklenemedi"}</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Onaysız Kullanıcılar</Typography>
          <Typography variant="caption" color="text.secondary">
            Kayıt olup e-posta doğrulamasını tamamlamamış kullanıcılar. Doğrulama kodu 3 dakika geçerli olduğu için toplu
            mailde kod gönderilmez; kullanıcıya uygulamadan yeni kod istemesi anlatılır.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <MuiDatePicker
            label="Bu tarihten sonra kayıt olanlar"
            value={since}
            disableFuture
            onChange={(v: Dayjs | null) => {
              if (v && v.isValid()) setSearchParams({ since: v.startOf("day").toISOString() });
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <Button startIcon={<RefreshIcon />} onClick={() => revalidator.revalidate()} disabled={revalidator.state === "loading"}>
            Yenile
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard label="Onaysız kullanıcı" value={summary.total} color={summary.total > 0 ? "warning.main" : undefined} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard label="Henüz kurtarma maili gitmemiş" value={summary.notYetNotified} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard label="Seçili" value={selectedIds.length} />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<OutgoingMailIcon />} disabled={summary.total === 0} onClick={() => { setMailPreview(null); setMailOpen(true); }}>
          Kurtarma maili kuyruğa al
        </Button>
        <Button variant="outlined" color="warning" startIcon={<MarkEmailReadIcon />} disabled={summary.total === 0} onClick={() => { setConfirmPreview(null); setConfirmOpen(true); }}>
          {confirmScopeSelected ? `Seçili ${selectedIds.length} kullanıcıyı onayla` : "Listedekilerin tümünü onayla"}
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        rows={users}
        getRowId={(r) => r.userId}
        columns={columns}
        checkboxSelection
        rowSelectionModel={selection}
        onRowSelectionModelChange={(m) => setSelection(m)}
        density="compact"
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
        loading={revalidator.state === "loading"}
        sx={{ width: "100%" }}
      />

      {/* Kurtarma maili dialog */}
      <Dialog open={mailOpen} onClose={() => !busy && setMailOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Kurtarma maili kuyruğa al</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {since.format("DD.MM.YYYY")} tarihinden sonra kayıt olan onaysız kullanıcılara "IDYA Kaydınızı Tamamlayın" maili
            gönderilir. Mailler Email Logları kuyruğuna zamana yayılarak yazılır; arka plan servisi her 5 dakikada vadesi gelenleri
            etkin mail hesaplarından gönderir. Aynı adrese ikinci kez gitmez.
          </Typography>
          <Stack spacing={2}>
            <TextField label="En fazla kaç kullanıcı" type="number" size="small" value={maxCount} onChange={(e) => setMaxCount(Number(e.target.value))} slotProps={{ htmlInput: { min: 1 } }} />
            <TextField
              label="Saatte kaç mail"
              type="number"
              size="small"
              value={perHour}
              onChange={(e) => setPerHour(Number(e.target.value))}
              helperText="Mail hesaplarının günlük kotasını tek seferde tüketmemek için gönderim bu hıza yayılır."
              slotProps={{ htmlInput: { min: 1 } }}
            />
            <FormControlLabel
              control={<Checkbox checked={includeNotified} onChange={(e) => setIncludeNotified(e.target.checked)} />}
              label="Daha önce kurtarma maili kuyruğa alınanlara da tekrar gönder"
            />
            {mailPreview && (
              <Alert severity={mailPreview.queued > 0 ? "info" : "warning"}>
                Önizleme: <strong>{mailPreview.queued}</strong> kullanıcıya gönderilecek, {mailPreview.skipped} kullanıcı atlanacak
                {mailPreview.queued > 0 && perHour > 0 && (
                  <>
                    . Tahmini süre: yaklaşık {Math.ceil((mailPreview.queued / perHour) * 60)} dakika.
                  </>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMailOpen(false)} disabled={busy}>
            Vazgeç
          </Button>
          <Button onClick={() => runRecoveryMail(true)} disabled={busy || maxCount <= 0 || perHour <= 0}>
            Önizle
          </Button>
          <Button variant="contained" onClick={() => runRecoveryMail(false)} disabled={busy || !mailPreview || mailPreview.queued === 0}>
            Kuyruğa al ({mailPreview?.queued ?? 0})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toplu onay dialog */}
      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>E-postaları doğrulama olmadan onayla</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Bu işlem {confirmScopeSelected ? `seçili ${selectedIds.length} kullanıcının` : `${since.format("DD.MM.YYYY")} tarihinden sonra kayıt olan tüm onaysız kullanıcıların`} e-postasını
            doğrulama kodu olmadan onaylar. Kullanıcılar hemen giriş yapabilir; ancak yanlış yazılmış e-posta adresleri de
            onaylanmış olur. Geri alınamaz.
          </Alert>
          {confirmPreview && (
            <Alert severity="info">
              Önizleme: <strong>{confirmPreview.confirmed}</strong> kullanıcı onaylanacak.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>
            Vazgeç
          </Button>
          <Button onClick={() => runConfirm(true)} disabled={busy}>
            Önizle
          </Button>
          <Button variant="contained" color="warning" onClick={() => runConfirm(false)} disabled={busy || !confirmPreview || confirmPreview.confirmed === 0}>
            Onayla ({confirmPreview?.confirmed ?? 0})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
